import React, { useState } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  MagnifyingGlassIcon,
  BookOpenIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SparklesIcon,
  LightBulbIcon,
  BeakerIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';
import { Button } from '../../components/forms/Buttons/Button';

// Presentation-only class tokens shared by the views below
const cardCls = 'rounded-2xl border border-slate-200 bg-white shadow-sm';
const controlCls =
  'block w-full rounded-lg border-slate-300 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15';
const selectCls =
  'block h-10 w-full rounded-lg border-slate-300 py-0 pl-3 pr-10 text-sm text-slate-700 shadow-sm transition duration-200 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 sm:w-auto';
const labelCls = 'mb-1.5 block text-sm font-medium text-slate-700';
const thCls = 'px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500';
const thSortCls = `${thCls} cursor-pointer select-none text-left transition-colors duration-200 hover:bg-slate-100 hover:text-slate-700`;
const backBtnCls =
  'mb-4 -ml-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-500 transition duration-200 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600';
const pageBtnCls =
  'inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-lg px-3 text-sm font-medium transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50';
const pageBtnIdleCls = 'bg-white text-slate-700 ring-1 ring-inset ring-slate-300 enabled:hover:bg-slate-50';
const badgeCls = 'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset';
const difficultyCls = (difficulty) =>
  difficulty === 'Легкий' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' :
  difficulty === 'Средний' ? 'bg-amber-50 text-amber-700 ring-amber-600/20' :
  'bg-red-50 text-red-700 ring-red-600/20';

const Exams = () => {
  // Моковые данные вопросов (база вопросов)
  const [questionBank] = useState([
    { id: 1, text: 'Что такое производная функции?', type: 'multiple', subject: 'Математика', difficulty: 'Средний' },
    { id: 2, text: 'Решите уравнение x² + 5x + 6 = 0', type: 'multiple', subject: 'Математика', difficulty: 'Легкий' },
    { id: 3, text: 'Найдите интеграл от функции x²', type: 'open', subject: 'Математика', difficulty: 'Сложный' },
    { id: 4, text: 'Что такое переменная в программировании?', type: 'multiple', subject: 'Программирование', difficulty: 'Легкий' },
    { id: 5, text: 'Объясните принципы ООП', type: 'multiple', subject: 'Программирование', difficulty: 'Средний' },
    { id: 6, text: 'Перечислите принципы SOLID', type: 'open', subject: 'Программирование', difficulty: 'Сложный' },
    { id: 7, text: 'Что такое Big O нотация?', type: 'open', subject: 'Алгоритмы', difficulty: 'Средний' },
    { id: 8, text: 'Разница между массивом и списком', type: 'multiple', subject: 'Структуры данных', difficulty: 'Легкий' },
    { id: 9, text: 'Что такое рекурсия?', type: 'open', subject: 'Алгоритмы', difficulty: 'Средний' },
    { id: 10, text: 'Принципы работы с базами данных', type: 'multiple', subject: 'Базы данных', difficulty: 'Сложный' }
  ]);

  const [exams, setExams] = useState([
    { 
      id: 1, 
      name: 'Вступительный экзамен по математике', 
      description: 'Комплексный экзамен для поступающих в университет',
      category: 'Вступительные экзамены',
      status: 'Активный',
      createdAt: '2024-01-15',
      tests: [
        { 
          id: 1, 
          name: 'Алгебра и начала анализа', 
          description: 'Основы алгебры и математического анализа',
          questions: [1, 2, 3],
          duration: 90
        },
        { 
          id: 2, 
          name: 'Геометрия', 
          description: 'Планиметрия и стереометрия',
          questions: [],
          duration: 60
        }
      ]
    },
    { 
      id: 2, 
      name: 'Тестирование программистов', 
      description: 'Оценка навыков программирования для кандидатов',
      category: 'Профессиональные тесты',
      status: 'Черновик',
      createdAt: '2024-02-20',
      tests: [
        { 
          id: 3, 
          name: 'Основы программирования', 
          description: 'Базовые концепции и принципы',
          questions: [4, 5],
          duration: 120
        }
      ]
    },
    { 
      id: 3, 
      name: 'Экзамен по алгоритмам', 
      description: 'Проверка знаний алгоритмов и структур данных',
      category: 'Технические тесты',
      status: 'Активный',
      createdAt: '2024-03-10',
      tests: []
    },
    { 
      id: 4, 
      name: 'Базы данных - продвинутый уровень', 
      description: 'Глубокие знания SQL и NoSQL',
      category: 'Профессиональные тесты',
      status: 'Черновик',
      createdAt: '2024-03-15',
      tests: []
    }
  ]);

  const [currentView, setCurrentView] = useState('list');
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', category: '', duration: 60 });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('all');
  
  // CRM фильтры
  const [examSearchTerm, setExamSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Получение уникальных предметов
  const subjects = ['all', ...new Set(questionBank.map(q => q.subject))];
  const categories = ['all', ...new Set(exams.map(e => e.category))];

  // Фильтрация и сортировка экзаменов
  const filteredExams = exams.filter(exam => {
    const matchesSearch = exam.name.toLowerCase().includes(examSearchTerm.toLowerCase()) ||
                         exam.description.toLowerCase().includes(examSearchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || exam.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || exam.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const sortedExams = [...filteredExams].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Пагинация
  const totalPages = Math.ceil(sortedExams.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedExams = sortedExams.slice(startIndex, startIndex + itemsPerPage);

  // Функции сортировки
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Создание нового экзамена
  const createExam = () => {
    if (formData.name.trim()) {
      const newExam = {
        id: Date.now(),
        name: formData.name,
        description: formData.description,
        category: formData.category || 'Общие',
        status: 'Черновик',
        createdAt: new Date().toISOString().split('T')[0],
        tests: []
      };
      setExams([...exams, newExam]);
      setFormData({ name: '', description: '', category: '', duration: 60 });
      setCurrentView('list');
    }
  };

  // Создание нового теста
  const createTest = () => {
    if (formData.name.trim() && selectedExam) {
      const newTest = {
        id: Date.now(),
        name: formData.name,
        description: formData.description,
        questions: [],
        duration: formData.duration
      };
      
      const updatedExams = exams.map(exam => 
        exam.id === selectedExam.id 
          ? { ...exam, tests: [...exam.tests, newTest] }
          : exam
      );
      
      setExams(updatedExams);
      setSelectedExam(updatedExams.find(exam => exam.id === selectedExam.id));
      setFormData({ name: '', description: '', category: '', duration: 60 });
      setCurrentView('view-exam');
    }
  };

  // Добавление вопроса к тесту
  const addQuestionToTest = (questionId) => {
    if (selectedTest && selectedExam && !selectedTest.questions.includes(questionId)) {
      const updatedExams = exams.map(exam => 
        exam.id === selectedExam.id 
          ? {
              ...exam,
              tests: exam.tests.map(test => 
                test.id === selectedTest.id
                  ? { ...test, questions: [...test.questions, questionId] }
                  : test
              )
            }
          : exam
      );
      
      setExams(updatedExams);
      setSelectedExam(updatedExams.find(exam => exam.id === selectedExam.id));
      setSelectedTest(
        updatedExams
          .find(exam => exam.id === selectedExam.id)
          .tests.find(test => test.id === selectedTest.id)
      );
    }
  };

  // Удаление экзамена
  const deleteExam = (examId) => {
    setExams(exams.filter(exam => exam.id !== examId));
  };

  // Фильтрация вопросов
  const filteredQuestions = questionBank.filter(question => {
    const matchesSearch = question.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         question.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSubject = selectedSubject === 'all' || question.subject === selectedSubject;
    return matchesSearch && matchesSubject;
  });

  // Получение вопроса по ID
  const getQuestionById = (id) => questionBank.find(q => q.id === id);

  // CRM стиль - Список экзаменов в виде таблицы
  const ExamsList = () => (
    <div>
      {/* Заголовок и кнопка создания */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Управление экзаменами</h1>
          <p className="mt-1 text-sm text-slate-500">Полный список экзаменов в системе</p>
        </div>
        <Button
          color="primary"
          onClick={() => setCurrentView('create-exam')}
          className="shrink-0 text-sm"
        >
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
          Создать экзамен
        </Button>
      </div>

      {/* Фильтры и поиск */}
      <div className={`${cardCls} mb-6 p-4`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <div className="min-w-0 sm:min-w-[18rem] sm:flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                type="text"
                value={examSearchTerm}
                onChange={(e) => setExamSearchTerm(e.target.value)}
                placeholder="Поиск экзаменов..."
                className={`${controlCls} h-10 pl-10 pr-4`}
              />
            </div>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={selectCls}
          >
            <option value="all">Все статусы</option>
            <option value="Активный">Активный</option>
            <option value="Черновик">Черновик</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={selectCls}
          >
            <option value="all">Все категории</option>
            {categories.filter(c => c !== 'all').map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Таблица экзаменов */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[56rem]">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th
                  className={thSortCls}
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    Название
                    {sortField === 'name' && (
                      sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 text-blue-600" /> : <ChevronDownIcon className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                </th>
                <th className={`${thCls} text-left`}>
                  Категория
                </th>
                <th className={`${thCls} text-left`}>
                  Статус
                </th>
                <th
                  className={thSortCls}
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center gap-1">
                    Дата создания
                    {sortField === 'createdAt' && (
                      sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 text-blue-600" /> : <ChevronDownIcon className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                </th>
                <th className={`${thCls} text-center`}>
                  Тестов
                </th>
                <th className={`${thCls} text-center`}>
                  Вопросов
                </th>
                <th className={`${thCls} text-right`}>
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {paginatedExams.map((exam, index) => (
                <tr key={exam.id} className="transition-colors duration-200 hover:bg-slate-50/80">
                  <td className="px-6 py-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900">{exam.name}</div>
                      {exam.description && (
                        <div className="mt-0.5 max-w-xs truncate text-sm text-slate-500">{exam.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">{exam.category}</td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span className={`${badgeCls} ${
                      exam.status === 'Активный'
                        ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
                        : 'bg-amber-50 text-amber-700 ring-amber-600/20'
                    }`}>
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${exam.status === 'Активный' ? 'bg-emerald-500' : 'bg-amber-500'}`}
                        aria-hidden="true"
                      />
                      {exam.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm tabular-nums text-slate-700">
                    {new Date(exam.createdAt).toLocaleDateString('ru-RU')}
                  </td>
                  <td className="px-6 py-4 text-center text-sm font-medium tabular-nums text-slate-700">{exam.tests.length}</td>
                  <td className="px-6 py-4 text-center text-sm font-medium tabular-nums text-slate-700">
                    {exam.tests.reduce((acc, test) => acc + test.questions.length, 0)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => {
                          setSelectedExam(exam);
                          setCurrentView('view-exam');
                        }}
                        className="rounded-lg p-2 text-slate-400 transition duration-200 hover:bg-blue-50 hover:text-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        title="Просмотреть"
                        aria-label="Просмотреть"
                      >
                        <EyeIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => deleteExam(exam.id)}
                        className="rounded-lg p-2 text-slate-400 transition duration-200 hover:bg-red-50 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                        title="Удалить"
                        aria-label="Удалить"
                      >
                        <TrashIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Пагинация */}
        {totalPages > 1 && (
          <div className="flex flex-col gap-3 border-t border-slate-200 px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-600">
              Показано {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredExams.length)} из {filteredExams.length} экзаменов
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`${pageBtnCls} ${pageBtnIdleCls}`}
              >
                Предыдущая
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`${pageBtnCls} ${
                    currentPage === i + 1
                      ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/25'
                      : pageBtnIdleCls
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`${pageBtnCls} ${pageBtnIdleCls}`}
              >
                Следующая
              </button>
            </div>
          </div>
        )}
      </div>

      {filteredExams.length === 0 && (
        <div className={`${cardCls} mt-4 px-6 py-12 text-center`}>
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <AcademicCapIcon className="h-6 w-6" aria-hidden="true" />
          </div>
          <h3 className="text-sm font-medium text-slate-900">Экзамены не найдены</h3>
          <p className="mt-1 text-sm text-slate-500">Попробуйте изменить параметры поиска</p>
        </div>
      )}
    </div>
  );

  // Создание экзамена
  const CreateExamForm = () => (
    <div>
      <button
        onClick={() => setCurrentView('list')}
        className={backBtnCls}
      >
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
        Назад к экзаменам
      </button>

      <div className={`${cardCls} max-w-3xl p-6 sm:p-8`}>
        <h1 className="mb-6 text-2xl font-semibold tracking-tight text-slate-900">Создать новый экзамен</h1>

        <div className="max-w-2xl space-y-5">
          <div>
            <label className={labelCls}>
              Название экзамена *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`${controlCls} h-10 px-3`}
              placeholder="Введите название экзамена"
            />
          </div>

          <div>
            <label className={labelCls}>
              Описание
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="4"
              className={`${controlCls} resize-none px-3 py-2`}
              placeholder="Описание экзамена"
            />
          </div>

          <div>
            <label className={labelCls}>
              Категория
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className={`${controlCls} h-10 py-0 pl-3 pr-10`}
            >
              <option value="">Выберите категорию</option>
              <option value="Вступительные экзамены">Вступительные экзамены</option>
              <option value="Профессиональные тесты">Профессиональные тесты</option>
              <option value="Технические тесты">Технические тесты</option>
              <option value="Сертификация">Сертификация</option>
              <option value="Общие">Общие</option>
            </select>
          </div>
          
          <div className="flex flex-col gap-3 border-t border-slate-100 pt-6 sm:flex-row">
            <Button
              color="primary"
              onClick={createExam}
              className="text-sm"
            >
              Создать экзамен
            </Button>
            <Button
              color="secondary-outline"
              onClick={() => {
                setCurrentView('list');
                setFormData({ name: '', description: '', category: '', duration: 60 });
              }}
              className="text-sm"
            >
              Отмена
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // Просмотр экзамена (оставляем как есть)
  const ViewExam = () => (
    <div>
      <button
        onClick={() => setCurrentView('list')}
        className={backBtnCls}
      >
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
        Назад к экзаменам
      </button>

      <div className={`${cardCls} p-5 sm:p-6`}>
        <div className="mb-6 flex flex-col gap-4 border-b border-slate-100 pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-sm shadow-blue-600/25">
              <AcademicCapIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{selectedExam?.name}</h1>
              <p className="mt-1 text-sm text-slate-500">{selectedExam?.description}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="text-sm text-slate-500">Категория: {selectedExam?.category}</span>
                <span className={`${badgeCls} ${
                  selectedExam?.status === 'Активный'
                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
                    : 'bg-amber-50 text-amber-700 ring-amber-600/20'
                }`}>
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${selectedExam?.status === 'Активный' ? 'bg-emerald-500' : 'bg-amber-500'}`}
                    aria-hidden="true"
                  />
                  {selectedExam?.status}
                </span>
              </div>
            </div>
          </div>
          <Button
            color="primary"
            onClick={() => {
              setFormData({ name: '', description: '', category: '', duration: 60 });
              setCurrentView('create-test');
            }}
            className="shrink-0 text-sm"
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            Добавить тест
          </Button>
        </div>

        <div className="space-y-4">
          <h2 className="text-base font-semibold text-slate-900">Тесты в экзамене</h2>

          {selectedExam?.tests?.map(test => (
            <div key={test.id} className="rounded-xl border border-slate-200 p-4 sm:p-5">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <BookOpenIcon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-slate-900">{test.name}</h3>
                    <p className="text-sm text-slate-500">{test.description}</p>
                    <p className="mt-1 text-xs text-slate-400">Длительность: {test.duration} минут</p>
                  </div>
                </div>
                <Button
                  color="secondary-outline"
                  onClick={() => {
                    setSelectedTest(test);
                    setCurrentView('add-questions');
                  }}
                  className="shrink-0 text-sm"
                >
                  <PencilIcon className="h-4 w-4" aria-hidden="true" />
                  Редактировать
                </Button>
              </div>

              <div className="rounded-xl bg-slate-50 p-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Вопросы в тесте ({test.questions.length})</p>

                {test.questions.length > 0 ? (
                  <div className="space-y-2">
                    {test.questions.slice(0, 3).map(questionId => {
                      const question = getQuestionById(questionId);
                      return question ? (
                        <div key={questionId} className="rounded-lg bg-white p-3 shadow-sm ring-1 ring-slate-200">
                          <p className="text-sm text-slate-700">{question.text}</p>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="text-xs text-slate-500">{question.subject}</span>
                            <span className={`${badgeCls} ${difficultyCls(question.difficulty)}`}>
                              {question.difficulty}
                            </span>
                          </div>
                        </div>
                      ) : null;
                    })}
                    {test.questions.length > 3 && (
                      <p className="py-2 text-center text-xs text-slate-500">
                        ... и еще {test.questions.length - 3} вопросов
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="py-4 text-center">
                    <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-400 ring-1 ring-slate-200">
                      <DocumentTextIcon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <p className="text-sm text-slate-500">Вопросы не добавлены</p>
                  </div>
                )}
              </div>
            </div>
          ))}

          {selectedExam?.tests?.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 px-6 py-12 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <BookOpenIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-medium text-slate-900">В этом экзамене пока нет тестов</h3>
              <p className="mb-5 mt-1 text-sm text-slate-500">Создайте первый тест для начала работы</p>
              <Button
                color="primary"
                onClick={() => {
                  setFormData({ name: '', description: '', category: '', duration: 60 });
                  setCurrentView('create-test');
                }}
                className="text-sm"
              >
                Создать тест
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Создание теста
  const CreateTestForm = () => (
    <div>
      <button
        onClick={() => setCurrentView('view-exam')}
        className={backBtnCls}
      >
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
        Назад к экзамену
      </button>

      <div className={`${cardCls} max-w-3xl p-6 sm:p-8`}>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Создать новый тест</h1>
        <p className="mb-6 mt-1 text-sm text-slate-500">Для экзамена: <span className="font-medium text-slate-700">{selectedExam?.name}</span></p>

        <div className="max-w-2xl space-y-5">
          <div>
            <label className={labelCls}>
              Название теста *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`${controlCls} h-10 px-3`}
              placeholder="Введите название теста"
            />
          </div>

          <div>
            <label className={labelCls}>
              Описание
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="4"
              className={`${controlCls} resize-none px-3 py-2`}
              placeholder="Описание теста"
            />
          </div>

          <div>
            <label className={labelCls}>
              Длительность (минуты)
            </label>
            <input
              type="number"
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 60 })}
              className={`${controlCls} h-10 px-3 tabular-nums sm:max-w-[12rem]`}
              min="1"
              max="300"
            />
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-100 pt-6 sm:flex-row">
            <Button
              color="primary"
              onClick={createTest}
              className="text-sm"
            >
              Создать тест
            </Button>
            <Button
              color="secondary-outline"
              onClick={() => {
                setCurrentView('view-exam');
                setFormData({ name: '', description: '', category: '', duration: 60 });
              }}
              className="text-sm"
            >
              Отмена
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // Горизонтальное разделение для добавления вопросов
  const AddQuestions = () => {
    const [questionPage, setQuestionPage] = useState(1);
    const questionsPerPage = 15;

    const getQuestionIcon = (subject) => {
      const icons = {
        'Математика': SparklesIcon,
        'Программирование': CpuChipIcon,
        'Алгоритмы': LightBulbIcon,
        'Структуры данных': BeakerIcon,
        'Базы данных': DocumentTextIcon
      };
      return icons[subject] || DocumentTextIcon;
    };

    const getSubjectColor = (subject) => {
      const colors = {
        'Математика': { bg: 'bg-blue-50', border: 'ring-blue-600/20', text: 'text-blue-700', icon: 'bg-blue-50 text-blue-600' },
        'Программирование': { bg: 'bg-violet-50', border: 'ring-violet-600/20', text: 'text-violet-700', icon: 'bg-violet-50 text-violet-600' },
        'Алгоритмы': { bg: 'bg-emerald-50', border: 'ring-emerald-600/20', text: 'text-emerald-700', icon: 'bg-emerald-50 text-emerald-600' },
        'Структуры данных': { bg: 'bg-amber-50', border: 'ring-amber-600/20', text: 'text-amber-700', icon: 'bg-amber-50 text-amber-600' },
        'Базы данных': { bg: 'bg-slate-100', border: 'ring-slate-500/20', text: 'text-slate-700', icon: 'bg-slate-100 text-slate-600' }
      };
      return colors[subject] || { bg: 'bg-slate-50', border: 'ring-slate-500/20', text: 'text-slate-700', icon: 'bg-slate-100 text-slate-600' };
    };

    // Пагинация для вопросов
    const totalQuestionPages = Math.ceil(filteredQuestions.length / questionsPerPage);
    const startQuestionIndex = (questionPage - 1) * questionsPerPage;
    const paginatedQuestions = filteredQuestions.slice(startQuestionIndex, startQuestionIndex + questionsPerPage);

    return (
      <div className="flex flex-col lg:h-[calc(100vh-7rem)] lg:min-h-[32rem]">
        <div>
          <button
            onClick={() => setCurrentView('view-exam')}
            className={backBtnCls}
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            Назад к экзамену
          </button>
        </div>

        {/* Заголовок */}
        <div className={`${cardCls} mb-4 p-4 sm:p-5`}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-sm shadow-blue-600/25">
                <DocumentTextIcon className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-semibold tracking-tight text-slate-900">Управление вопросами</h1>
                <p className="truncate text-sm text-slate-500">Тест: <span className="font-medium text-slate-700">{selectedTest?.name}</span></p>
              </div>
            </div>

            {/* Статистика в заголовке */}
            <div className="flex gap-2">
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                  <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
                </div>
                <div>
                  <div className="text-lg font-semibold leading-tight tabular-nums text-slate-900">{selectedTest?.questions?.length || 0}</div>
                  <div className="text-xs text-slate-500">Добавлено</div>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <BookOpenIcon className="h-5 w-5" aria-hidden="true" />
                </div>
                <div>
                  <div className="text-lg font-semibold leading-tight tabular-nums text-slate-900">{questionBank.length}</div>
                  <div className="text-xs text-slate-500">Доступно</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Горизонтальное разделение */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">

          {/* Левая панель - Добавленные вопросы (1/3) */}
          <div className={`${cardCls} flex max-h-[28rem] flex-col overflow-hidden lg:max-h-none lg:w-1/3`}>
            <div className="border-b border-slate-200 px-4 py-3.5">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <CheckCircleIcon className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                Вопросы в тесте ({selectedTest?.questions?.length || 0})
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto">
              {selectedTest?.questions?.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {selectedTest.questions.map((questionId, index) => {
                    const question = getQuestionById(questionId);
                    const subjectColor = getSubjectColor(question?.subject);
                    const IconComponent = getQuestionIcon(question?.subject);
                    return question ? (
                      <div key={questionId} className="px-4 py-3 transition-colors duration-200 hover:bg-slate-50/80">
                        <div className="flex items-start gap-3">
                          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-slate-100 text-xs font-semibold tabular-nums text-slate-600">
                            {index + 1}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="mb-1.5 line-clamp-2 text-sm font-medium text-slate-900">{question.text}</p>
                            <div className="flex flex-wrap items-center gap-2">
                              <div className={`flex h-5 w-5 items-center justify-center rounded ${subjectColor.icon}`}>
                                <IconComponent className="h-3 w-3" aria-hidden="true" />
                              </div>
                              <span className="text-xs text-slate-500">{question.subject}</span>
                              <span className={`${badgeCls} ${difficultyCls(question.difficulty)}`}>
                                {question.difficulty}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              ) : (
                <div className="px-6 py-10 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                    <DocumentTextIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <p className="text-sm font-medium text-slate-900">Вопросы не добавлены</p>
                  <p className="mt-1 text-sm text-slate-500">Добавьте вопросы из списка справа</p>
                </div>
              )}
            </div>
          </div>

          {/* Правая панель - Доступные вопросы (2/3) */}
          <div className={`${cardCls} flex min-h-0 flex-col overflow-hidden lg:w-2/3`}>

            {/* Фильтры */}
            <div className="border-b border-slate-200 p-4">
              <div className="mb-3 flex flex-col gap-3 sm:flex-row">
                <div className="relative min-w-0 flex-1">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Поиск вопросов..."
                    className={`${controlCls} h-10 pl-9 pr-3`}
                  />
                </div>
                <select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  className={selectCls}
                >
                  <option value="all">Все предметы</option>
                  {subjects.filter(s => s !== 'all').map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-slate-900">Доступные вопросы</h3>
                <span className="text-sm tabular-nums text-slate-500">
                  {startQuestionIndex + 1}-{Math.min(startQuestionIndex + questionsPerPage, filteredQuestions.length)} из {filteredQuestions.length}
                </span>
              </div>
            </div>

            {/* Список вопросов */}
            <div className="flex-1 overflow-y-auto">
              {paginatedQuestions.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {paginatedQuestions.map((question) => {
                    const isAdded = selectedTest?.questions?.includes(question.id);
                    const subjectColor = getSubjectColor(question.subject);
                    const IconComponent = getQuestionIcon(question.subject);

                    return (
                      <div key={question.id} className={`px-4 py-3 transition-colors duration-200 ${
                        isAdded
                          ? 'bg-emerald-50/50'
                          : 'hover:bg-slate-50/80'
                      }`}>
                        <div className="flex items-center gap-3">
                          {/* Иконка предмета */}
                          <div className={`hidden h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl sm:flex ${subjectColor.icon}`}>
                            <IconComponent className="h-5 w-5" aria-hidden="true" />
                          </div>

                          {/* Основная информация */}
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex flex-wrap items-center gap-2">
                              <span className={`${badgeCls} ${subjectColor.bg} ${subjectColor.text} ${subjectColor.border}`}>
                                {question.type === 'multiple' ? 'Выбор' : 'Открытый'}
                              </span>
                              <span className={`${badgeCls} ${difficultyCls(question.difficulty)}`}>
                                {question.difficulty}
                              </span>
                              <span className="text-xs text-slate-500">{question.subject}</span>
                            </div>
                            <p className="line-clamp-2 text-sm font-medium text-slate-900">{question.text}</p>
                          </div>

                          {/* Кнопка действия */}
                          <button
                            onClick={() => addQuestionToTest(question.id)}
                            disabled={isAdded}
                            className={`inline-flex h-8 flex-shrink-0 items-center rounded-lg px-3 text-xs font-medium transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${
                              isAdded
                                ? 'cursor-not-allowed bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20'
                                : 'bg-blue-600 text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700'
                            }`}
                          >
                            {isAdded ? (
                              <span className="flex items-center gap-1">
                                <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                                Добавлено
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                                Добавить
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-6 py-12 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                    <DocumentTextIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 className="text-sm font-medium text-slate-900">Вопросы не найдены</h3>
                  <p className="mt-1 text-sm text-slate-500">Попробуйте изменить параметры поиска</p>
                </div>
              )}
            </div>

            {/* Пагинация */}
            {totalQuestionPages > 1 && (
              <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-sm text-slate-600">
                  Страница {questionPage} из {totalQuestionPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setQuestionPage(Math.max(1, questionPage - 1))}
                    disabled={questionPage === 1}
                    className={`${pageBtnCls} ${pageBtnIdleCls}`}
                  >
                    Назад
                  </button>
                  <button
                    onClick={() => setQuestionPage(Math.min(totalQuestionPages, questionPage + 1))}
                    disabled={questionPage === totalQuestionPages}
                    className={`${pageBtnCls} ${pageBtnIdleCls}`}
                  >
                    Вперед
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Основной рендер
  // The views above are declared inside Exams, so they remount on every render: the fade sits on this
  // wrapper, keyed by view, so it plays when the view changes rather than on every keystroke.
  return (
    <div key={currentView} className="w-full motion-safe:animate-fade-in">
      {currentView === 'list' && <ExamsList />}
      {currentView === 'create-exam' && <CreateExamForm />}
      {currentView === 'view-exam' && <ViewExam />}
      {currentView === 'create-test' && <CreateTestForm />}
      {currentView === 'add-questions' && <AddQuestions />}
    </div>
  );
};

export default Exams